import { useMemo, useEffect } from 'react';
import type { TutorialDefinition } from '../types';
import { TutorialStateMachine } from '../stateMachine/TutorialStateMachine';
import { TutorialEngine, type TutorialEngineConfig } from '../engine/TutorialEngine';
import { createDefaultActionRegistry } from '../engine/createDefaultRegistry';
import {
  BrowserDOMAdapter,
  BrowserTimerAdapter,
  BrowserAnimationAdapter,
  BrowserRippleAdapter,
} from '../adapters';
import type { ActionContext } from '../registry/ActionRegistry';

export interface UseTutorialEngineOptions {
  tutorial: TutorialDefinition;
  engineConfig?: TutorialEngineConfig;
  onAnalytics?: (event: string, data: Record<string, unknown>) => void;
}

export interface TutorialEngineInstance {
  stateMachine: TutorialStateMachine;
  engine: TutorialEngine;
  adapters: ActionContext;
}

/**
 * React hook for tutorial system
 * Creates and configures the tutorial engine with all dependencies
 */
export function useTutorialEngine(options: UseTutorialEngineOptions): TutorialEngineInstance {
  const { tutorial, engineConfig, onAnalytics } = options;

  // Create adapters (memoized)
  const adapters = useMemo<ActionContext>(() => {
    return {
      domAdapter: new BrowserDOMAdapter(),
      timerAdapter: new BrowserTimerAdapter(),
      animationAdapter: new BrowserAnimationAdapter(),
      rippleAdapter: new BrowserRippleAdapter(),
    };
  }, []);

  // Create state machine (memoized by tutorial.id)
  const stateMachine = useMemo(() => {
    return new TutorialStateMachine(tutorial.steps);
  }, [tutorial.id, tutorial.steps]);

  // Create engine (memoized by tutorial.id)
  const engine = useMemo(() => {
    const registry = createDefaultActionRegistry();

    const config: TutorialEngineConfig = {
      ...engineConfig,
      onActionStart: (action) => {
        onAnalytics?.('tutorial_action_start', {
          tutorialId: tutorial.id,
          actionType: action.type,
          actionId: action.id,
        });
        engineConfig?.onActionStart?.(action);
      },
      onActionSuccess: (action, result) => {
        onAnalytics?.('tutorial_action_success', {
          tutorialId: tutorial.id,
          actionType: action.type,
          actionId: action.id,
          attempts: result.attempts,
          duration: result.duration,
        });
        engineConfig?.onActionSuccess?.(action, result);
      },
      onActionError: (action, error, result) => {
        onAnalytics?.('tutorial_action_error', {
          tutorialId: tutorial.id,
          actionType: action.type,
          actionId: action.id,
          error: error.message,
          attempts: result.attempts,
          duration: result.duration,
        });
        engineConfig?.onActionError?.(action, error, result);
      },
    };

    return new TutorialEngine(registry, adapters, config);
  }, [tutorial.id, adapters, engineConfig, onAnalytics]);

  // Cleanup state machine listeners on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      stateMachine.clearListeners();
    };
  }, [stateMachine]);

  return {
    stateMachine,
    engine,
    adapters,
  };
}
